import { Link } from 'wouter';
import { ArrowRight, MapPin } from 'lucide-react';
import type { Locality, Region } from '@shared/schema';
import { getGeographyTheme } from '@shared/city-theme';

interface RegionDiscoveryCardProps {
  region: Region;
  localities: Pick<Locality, 'id' | 'name' | 'slug'>[];
  onSelectRegion: () => void;
  onSelectLocality: (localitySlug: string) => void;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function RegionDiscoveryCard({
  region,
  localities,
  onSelectRegion,
  onSelectLocality,
}: RegionDiscoveryCardProps) {
  const regionTheme = getGeographyTheme(region.slug);
  const { colors: rc, components: rcomp, shape: rshape } = regionTheme;

  return (
    <div
      className="geo-card flex flex-col"
      style={
        {
          '--geo-card-bg': rcomp.cardBg,
          '--geo-card-border': rcomp.cardBorder,
          '--geo-card-radius': rcomp.cardRadius,
          '--geo-card-shadow': rshape.shadowCard,
          '--geo-card-shadow-hover': rshape.shadowHover,
          '--geo-card-hover-bg': hexToRgba(rc.primary, 0.05),
        } as React.CSSProperties
      }
    >
      {/* ── Clickable region header ── */}
      <button
        type="button"
        onClick={onSelectRegion}
        className="geo-card-header group flex items-start justify-between gap-4 p-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        style={{
          borderTopLeftRadius: rcomp.cardRadius,
          borderTopRightRadius: rcomp.cardRadius,
        }}
        aria-label={`Browse all classes in ${region.name}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: rc.primary }} />
            <span
              className="font-condensed text-xs font-semibold uppercase tracking-widest"
              style={{ color: rc.mutedForeground }}
            >
              Region
            </span>
          </div>
          <h3
            className="mt-2 text-2xl font-semibold leading-tight"
            style={{ fontFamily: regionTheme.typescale.headingStack, color: rc.foreground }}
          >
            {region.name}
          </h3>
          {region.description && (
            <p className="mt-1.5 text-sm leading-relaxed" style={{ color: rc.mutedForeground }}>
              {region.description}
            </p>
          )}
        </div>
        <span
          className="mt-1 inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-full px-5 py-3 text-base font-semibold shadow-sm transition-transform duration-200 group-hover:translate-x-0.5"
          style={{
            backgroundColor: rcomp.btnPrimaryBg,
            color: rcomp.btnPrimaryFg,
            border: rcomp.btnPrimaryBorder,
          }}
        >
          Browse all
          <ArrowRight className="h-5 w-5" />
        </span>
      </button>

      {/* ── Locality pills ── */}
      {localities.length > 0 && (
        <div className="px-6 py-4" style={{ borderTop: rcomp.cardBorder }}>
          <p
            className="mb-3 font-condensed text-xs font-semibold uppercase tracking-widest"
            style={{ color: rc.mutedForeground }}
          >
            Go directly to a neighborhood
          </p>
          <div className="flex flex-wrap gap-2">
            {localities.map(locality => {
              const lt = getGeographyTheme(locality.slug);
              // Tinted border uses the locality primary at 28% — signals identity without being loud
              const pillBorder = hexToRgba(lt.colors.primary, 0.28);
              const pillHoverBorder = lt.colors.primary;
              return (
                <Link
                  key={locality.id}
                  href={`/regions/${region.slug}/${locality.slug}`}
                  onClick={() => onSelectLocality(locality.slug)}
                  className="geo-pill inline-flex min-h-[44px] items-center gap-1.5 border px-4 py-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  style={
                    {
                      '--geo-pill-bg': lt.components.badgeBg,
                      '--geo-pill-fg': lt.components.badgeFg,
                      '--geo-pill-border': pillBorder,
                      '--geo-pill-radius': lt.components.badgeRadius,
                      '--geo-pill-hover-bg': hexToRgba(lt.colors.primary, 0.1),
                      '--geo-pill-hover-fg': lt.colors.primary,
                      '--geo-pill-hover-border': pillHoverBorder,
                    } as React.CSSProperties
                  }
                >
                  <MapPin className="h-3 w-3 shrink-0" style={{ color: lt.components.badgeFg }} />
                  {locality.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
